import React from 'react';

import type { CSS } from '@components-shared/src/stitches';
import { styled, theme } from '@components-shared/src/stitches';
import { TabContent } from '@components/main-components/tabs/Tab';

export const SectionTitle = ({ title }: { title: string }) => {
  return <div className="font-title text-title-m font-medium md:text-title-l">{title}</div>;
};

export const SectionInfo = styled('h3', {
  base: 'L',
  color: '$neutral500',
  fontStyle: 'regular',
  marginTop: 8,
});

export const HeaderContainer = styled('div', {
  width: '100%',
});

export const Fade = ({
  length,
  topOpacity = 0,
  endOpacity = 1,
  topPercent = 0,
  bottomPercent = 100,
  lengthStartColor = '#FFFFFF',
  lengthEndColor = 'rgba(255, 255, 255, 0)',
  css = {},
  children,
  ignorePointerEvents = false,
}: {
  length?: number;
  topOpacity?: number;
  endOpacity?: number;
  topPercent?: number;
  bottomPercent?: number;
  lengthStartColor?: string;
  lengthEndColor?: string;
  css?: CSS;
  children?: React.ReactNode;
  ignorePointerEvents?: boolean;
}) => {
  const backgroundImage = !!length
    ? `linear-gradient(270deg, ${lengthStartColor} ${length}%, ${lengthEndColor} 100%)`
    : `linear-gradient(180deg, rgba(255, 255, 255, ${topOpacity}) ${topPercent}%, rgba(255, 255, 255, ${endOpacity}) ${bottomPercent}%)`;
  return (
    <FadeContainer
      ignorePointerEvents={ignorePointerEvents}
      css={{
        background: backgroundImage,
        '@tabletLandscape': {
          maxWidth: !length ? 703 : 'unset',
        },
        ...css,
      }}
    >
      {children}
    </FadeContainer>
  );
};

const FadeContainer = styled('div', {
  height: '100%',
  width: '100%',

  variants: {
    ignorePointerEvents: {
      true: {
        pointerEvents: 'none',
      },
    },
  },
});

export const FlexToggle = styled('div', {
  display: 'none',
  variants: {
    media: {
      laptop: {
        '@laptop': {
          display: 'flex',
        },
      },
      notLaptop: {
        display: 'flex',
        '@laptop': {
          display: 'none',
        },
      },
      tablet: {
        '@tablet': {
          display: 'flex',
        },
      },
      notTablet: {
        display: 'flex',
        '@tablet': {
          display: 'none',
        },
      },
    },
  },
});

export const SongPageContainer = styled('div', {
  background: '$white',
  variants: {
    isAudioPlayerHidden: {
      true: {
        paddingBottom: 48,
      },
      false: {
        paddingBottom: 120,

        '@tablet': {
          paddingBottom: 130,
        },
      },
    },
  },
});

export const SharedWrapper = styled('div', {
  paddingX: 16,
  '@tablet': {
    paddingX: 24,
  },
});

export const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  marginTop: 0,
});

export const InfoContainer = styled('div', {
  '@tablet': {
    gap: 32,
    marginTop: 16,
  },
  '@laptop': {
    display: 'flex',
    maxWidth: theme.widths.landingMaxWidth,
    margin: '0 auto',
    gap: 64,
  },
});

export const LeftContainer = styled('div', {
  maxWidth: theme.widths.landingMaxWidth,
  display: 'flex',
  width: '100%',
  minWidth: 0,
  overflow: 'visible',
});

export const RightContainer = styled('div', {
  display: 'none',
  width: 382,
  flexShrink: 0,
  '&>div': {
    width: '100%',
  },
  '@tablet': {
    paddingRight: 24,
  },
  '@laptop': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    // Avoid setting width to 100% so left container can extend to it's max width
    height: '100%',
    marginTop: 0,
    position: 'sticky',
    top: 32,
  },
});

export const Sections = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'visible',
});

export const ActionsSection = styled('div', {
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  alignItems: 'center',
  '@tabletLandscape': {
    width: 'unset',
  },
});

export const Dropdowns = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

export const ArtistSection = styled('div', {
  display: 'none',
  '@laptop': {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  },
});

export const ReleaseActionsContainer = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 12,
});

export const StyledTabContent = styled(TabContent, {
  width: '100%',
  maxWidth: theme.widths.landingMaxWidth,
  margin: '0 auto',
  paddingTop: 32,
});
